import {
  AudioOutlined,
  BranchesOutlined,
  CheckCircleOutlined,
  ClockCircleOutlined,
  ExportOutlined,
  FileTextOutlined,
  HistoryOutlined,
  MessageOutlined,
  NodeIndexOutlined,
  PhoneOutlined,
  RightOutlined,
  TeamOutlined,
} from '@ant-design/icons';
import {
  Alert,
  Badge,
  Button,
  Card,
  Collapse,
  Divider,
  Empty,
  List,
  Segmented,
  Space,
  Spin,
  Steps,
  Tag,
  Timeline,
  Tooltip,
  Typography,
} from 'antd';
import axios from 'axios';
import {
  CSSProperties,
  Fragment,
  ReactNode,
  useEffect,
  useMemo,
  useRef,
  useState,
} from 'react';
import {
  actorLabel,
  eventLabel,
  LedgerFact,
} from '../../Insights/LeadEngagementLedger';
import { sanitizeHTML } from '../../utils';
import {
  type CadenceEvaluation,
  CadenceEvaluationRow,
} from './cadence-timeline';
import {
  compareRoutingAttempts,
  EngagementCall,
  EngagementMessage,
  EngagementNote,
  EngagementTimelineEvent,
  formatElapsed,
  getCadenceOutreachAssociations,
  getRoutingAttemptKey,
  isaClaimedOffer,
  isaFirstOfferedAt,
  isAgentText,
  IsaOffer,
  isaOfferResultLabel,
  isaReplyAt,
  IsaText,
  isaThreadTexts,
  isaWaves,
  isaWinnerLines,
  IsaWorkflow,
  isaWorkflowTitle,
  isLiveTransferFollowUp,
  mergeTimelineEvents,
  parseTimelineTimestamp,
  partitionTimelineEvents,
  RoutingAttempt,
  RoutingCall,
  sentenceCase,
  ThreeWayConversation,
} from './engagement-timeline-events';
import { formatDateFromUTCtoAgent } from '../agentSms/utils';
import { formatDateWithZone } from './format-date';
import {
  getCallDirectionLabel,
  isIncomingCall,
} from './lead-conversation-call';
import { deliveryCaption } from './delivery-caption';
import { cadenceTraceUrl, phoenixTraceUrl } from './trace-link';
import './engagement-overview.css';
import { useAuthenticatedRequest } from '../../UserContext';

const { Text, Paragraph } = Typography;

interface EngagementTimelineProps {
  accessToken: string;
  apiUrl: string;
  campaignKey?: string | null;
  clientId: string;
  crmLink?: string;
  engagementId: string;
  leadId: string;
  agentId?: string;
  sourceId?: string;
  timezone: string;
}

interface LedgerResponse {
  facts: LedgerFact[];
}

interface IsaWorkflowsResponse {
  workflows: IsaWorkflow[];
}

interface AudioState {
  url?: string;
  loading?: boolean;
  error?: string;
}

type SourceFilter = 'all' | EngagementTimelineEvent['source'];

const SOURCE_FILTERS: { label: string; value: SourceFilter }[] = [
  { label: 'All', value: 'all' },
  { label: 'Messages', value: 'message' },
  { label: 'Calls', value: 'call' },
  { label: 'Routing', value: 'routing' },
  { label: 'Decisions', value: 'cadence' },
  { label: 'Ledger', value: 'ledger' },
  { label: 'Notes', value: 'note' },
  { label: 'ISA', value: 'isa' },
  { label: '3-Way', value: 'threeWay' },
];

const arrayResult = <T,>(result: PromiseSettledResult<{ data: T[] }>): T[] =>
  result.status === 'fulfilled' && Array.isArray(result.value.data)
    ? result.value.data
    : [];

const humanize = (value: string): string => value.replace(/[-_]/g, ' ');

const statusColor = (status?: string | null): string => {
  if (status === 'completed' || status === 'connected') {
    return 'green';
  }
  if (status === 'no_answer' || status === 'no-answer') {
    return 'orange';
  }
  if (status === 'busy' || status === 'failed') {
    return 'red';
  }
  return 'default';
};

const sourcePresentation: Record<
  EngagementTimelineEvent['source'],
  { background: string; color: string; icon: ReactNode; label: string }
> = {
  message: {
    background: '#e6f4ff',
    color: '#1677ff',
    icon: <MessageOutlined />,
    label: 'Message',
  },
  call: {
    background: '#e6fffb',
    color: '#08979c',
    icon: <PhoneOutlined />,
    label: 'Call',
  },
  routing: {
    background: '#f9f0ff',
    color: '#722ed1',
    icon: <BranchesOutlined />,
    label: 'Routing',
  },
  cadence: {
    background: '#f0f5ff',
    color: '#2f54eb',
    icon: <NodeIndexOutlined />,
    label: 'Engine decision',
  },
  ledger: {
    background: '#fffbe6',
    color: '#ad6800',
    icon: <HistoryOutlined />,
    label: 'Engagement state',
  },
  note: {
    background: '#f5f5f5',
    color: '#737373',
    icon: <FileTextOutlined />,
    label: 'Note',
  },
  isa: {
    background: '#f6ffed',
    color: '#389e0d',
    icon: <TeamOutlined />,
    label: 'ISA',
  },
  threeWay: {
    background: '#f9f0ff',
    color: '#722ed1',
    icon: <TeamOutlined />,
    label: '3-way text',
  },
};

const timelineDotColor = (event: EngagementTimelineEvent): string => {
  if (
    event.source === 'note' ||
    (event.source === 'message' && event.payload.incoming) ||
    (event.source === 'call' && isIncomingCall(event.payload))
  ) {
    return '#bfbfbf';
  }
  return sourcePresentation[event.source].color;
};

const EventSurface = ({
  children,
  flush = false,
  source,
}: {
  children: ReactNode;
  flush?: boolean;
  source: EngagementTimelineEvent['source'];
}) => (
  <div
    className={`engagement-timeline-event-surface${
      flush ? ' engagement-timeline-event-surface-flush' : ''
    }`}
    style={{
      background: source === 'cadence' ? '#f7f8ff' : '#fff',
      borderBottom: '1px solid #f0f0f0',
      margin: 0,
    }}
  >
    {children}
  </div>
);

const SourceIdentity = ({
  muted = false,
  source,
  title,
}: {
  muted?: boolean;
  source: EngagementTimelineEvent['source'];
  title?: ReactNode;
}) => {
  const presentation = sourcePresentation[source];
  const color = muted ? '#8c8c8c' : presentation.color;
  const background = muted ? 'transparent' : presentation.background;
  return (
    <Space align="start" size={8}>
      <span
        style={{
          alignItems: 'center',
          background,
          borderRadius: 6,
          color,
          display: 'inline-flex',
          fontSize: 14,
          height: 28,
          justifyContent: 'center',
          width: 28,
        }}
      >
        {presentation.icon}
      </span>
      <span style={{ display: 'flex', flexDirection: 'column', gap: 1 }}>
        <Text
          style={{
            color,
            fontSize: 11,
            fontWeight: 600,
            letterSpacing: 0.4,
            lineHeight: 1.2,
            textTransform: 'uppercase',
          }}
        >
          {presentation.label}
        </Text>
        {title ? <Text strong>{title}</Text> : null}
      </span>
    </Space>
  );
};

const PREVIEW_CHARACTER_LIMIT = 240;

const compactPreview = (value: string): string => {
  const compact = value.replace(/\s+/g, ' ').trim();
  return compact.length > PREVIEW_CHARACTER_LIMIT
    ? `${compact.slice(0, PREVIEW_CHARACTER_LIMIT - 1).trimEnd()}…`
    : compact;
};

const notePreview = (html: string): string => {
  const parsed = new DOMParser().parseFromString(
    sanitizeHTML(html),
    'text/html',
  );
  return compactPreview(parsed.body.textContent || '');
};

const routingAttemptsPreview = (attempts: RoutingAttempt[]): string => {
  const preview = attempts
    .slice(0, 3)
    .map(
      attempt =>
        `${attempt.agent_name || 'Unknown agent'} · ${humanize(
          attempt.was_routed ? 'connected' : attempt.call_status || 'pending',
        )}`,
    )
    .join(' · ');
  return attempts.length > 3
    ? `${preview} · ${attempts.length - 3} more`
    : preview;
};

const PreviewCollapse = ({
  children,
  label,
  preview,
}: {
  children: ReactNode;
  label: string;
  preview: string;
}) => (
  <Collapse
    bordered={false}
    className="engagement-timeline-contained-collapse engagement-timeline-preview-collapse"
    expandIconPosition="end"
    items={[
      {
        key: 'details',
        label: (
          <div className="engagement-timeline-collapse-label">
            <span className="engagement-timeline-collapse-title">{label}</span>
            {preview ? (
              <span
                aria-hidden="true"
                className="engagement-timeline-collapse-preview"
              >
                {preview}
              </span>
            ) : null}
          </div>
        ),
        children,
      },
    ]}
    size="small"
    style={{ background: 'transparent' }}
  />
);

const TimelineRow = ({
  source,
  title,
  timestamp,
  timezone,
  actions,
  children,
  muted = false,
}: {
  source: EngagementTimelineEvent['source'];
  title: ReactNode;
  timestamp: string;
  timezone: string;
  actions?: ReactNode;
  children?: ReactNode;
  muted?: boolean;
}) => (
  <EventSurface source={source}>
    <div
      style={{
        display: 'flex',
        justifyContent: 'space-between',
        alignItems: 'flex-start',
        flexWrap: 'wrap',
        gap: 8,
        marginBottom: 6,
      }}
    >
      <SourceIdentity muted={muted} source={source} title={title} />
      <Space className="engagement-timeline-event-meta" size="small" wrap>
        {actions}
        <Tooltip title={timezone}>
          <Text style={{ fontSize: 12, whiteSpace: 'nowrap' }} type="secondary">
            {formatDateWithZone(timestamp, timezone)}
          </Text>
        </Tooltip>
      </Space>
    </div>
    {children}
  </EventSurface>
);

const AudioControl = ({
  audio,
  label,
  onLoad,
}: {
  audio: AudioState | undefined;
  label: string;
  onLoad: () => void;
}) => {
  if (audio?.url) {
    return (
      <audio
        controls
        preload="none"
        src={audio.url}
        style={{ width: '100%', height: 34 }}
      />
    );
  }
  if (audio?.error) {
    return <Text type="secondary">{audio.error}</Text>;
  }
  return (
    <Button
      icon={<AudioOutlined />}
      loading={audio?.loading}
      onClick={onLoad}
      size="small"
    >
      {label}
    </Button>
  );
};

const MessageEvent = ({
  message,
  timezone,
}: {
  message: EngagementMessage;
  timezone: string;
}) => {
  const traceUrl = message.trace_ids?.[0]?.trace_id
    ? phoenixTraceUrl(message.trace_ids[0].trace_id)
    : null;
  return (
    <TimelineRow
      actions={
        traceUrl ? (
          <Button
            href={traceUrl}
            icon={<NodeIndexOutlined />}
            rel="noopener noreferrer"
            size="small"
            target="_blank"
          >
            View in Phoenix
          </Button>
        ) : undefined
      }
      source="message"
      timestamp={message.created_at}
      timezone={timezone}
      title={
        message.incoming
          ? 'Inbound'
          : message.sender_role === 'agent'
            ? 'Agent outbound'
            : 'Outbound'
      }
    >
      <div
        style={{
          display: 'flex',
          justifyContent: message.incoming ? 'flex-start' : 'flex-end',
        }}
      >
        <div
          className="engagement-timeline-message-bubble"
          style={{
            background: message.incoming
              ? '#e6f4ff'
              : message.sender_role === 'agent'
                ? '#722ed1'
                : '#1677ff',
            border: message.incoming
              ? '1px solid #91caff'
              : '1px solid transparent',
            borderRadius: message.incoming
              ? '16px 16px 16px 4px'
              : '16px 16px 4px 16px',
            color: message.incoming ? '#262626' : '#fff',
            lineHeight: 1.5,
            overflowWrap: 'anywhere',
            padding: '8px 12px',
            whiteSpace: 'pre-wrap',
          }}
        >
          {message.message}
        </div>
      </div>
      {deliveryCaption(message) && (
        <div
          style={{ display: 'flex', justifyContent: 'flex-end', marginTop: 4 }}
        >
          <Tag style={{ borderRadius: 10, fontSize: 11, margin: 0 }}>
            <Badge status="error" />
            {deliveryCaption(message)}
          </Tag>
        </div>
      )}
    </TimelineRow>
  );
};

const formatDuration = (seconds: number): string => {
  const minutes = Math.floor(seconds / 60);
  const remainder = seconds % 60;
  return minutes
    ? `${minutes}m${remainder ? ` ${remainder}s` : ''}`
    : `${remainder}s`;
};

const timelineEventDomId = (key: string): string =>
  `timeline-event-${key.replace(/[^a-zA-Z0-9_-]/g, '-')}`;

const CadenceActionOutcome = ({
  decisionTimestamp,
  event,
  linked,
  observedLabel,
  sourceAvailable,
  timezone,
}: {
  decisionTimestamp: string;
  event?: { key: string; timestamp: string };
  linked: boolean;
  observedLabel: string;
  sourceAvailable: boolean;
  timezone: string;
}) => {
  if (!sourceAvailable) {
    return (
      <Tooltip title="This activity source failed to load, so the outcome could not be verified">
        <Text style={{ color: '#737373', fontSize: 12 }}>Unavailable</Text>
      </Tooltip>
    );
  }
  if (!event) {
    return (
      <Tooltip title="No matching outbound event was observed before the next decision or re-evaluation">
        <Text style={{ color: '#d48806', fontSize: 12 }}>Not observed</Text>
      </Tooltip>
    );
  }
  const elapsed = formatDuration(
    Math.max(
      0,
      Math.round(
        (parseTimelineTimestamp(event.timestamp) -
          parseTimelineTimestamp(decisionTimestamp)) /
          1000,
      ),
    ),
  );
  const title = `${formatDateWithZone(event.timestamp, timezone)} · Closest outbound event before the next decision or re-evaluation`;
  if (!linked) {
    // The matching message/call exists but is filtered out of the current view, so its DOM
    // node isn't mounted — show the outcome without a jump link instead of a dead href.
    return (
      <Tooltip title={`${title} · Hidden by the current filter`}>
        <Text style={{ color: '#389e0d', fontSize: 12 }}>
          <CheckCircleOutlined /> {observedLabel} {elapsed} later
        </Text>
      </Tooltip>
    );
  }
  return (
    <Tooltip title={title}>
      <Button
        href={`#${timelineEventDomId(event.key)}`}
        icon={<CheckCircleOutlined />}
        size="small"
        style={{ color: '#389e0d', fontSize: 12, height: 'auto', padding: 0 }}
        type="link"
      >
        {observedLabel} {elapsed} later
      </Button>
    </Tooltip>
  );
};

const CallEvent = ({
  call,
  timezone,
  audio,
  onLoadAudio,
}: {
  call: EngagementCall;
  timezone: string;
  audio: AudioState | undefined;
  onLoadAudio: () => void;
}) => {
  const incoming = isIncomingCall(call);
  const result = call.post_call_analysis_result?.call_result;
  const outcome = result
    ? humanize(result)
    : call.is_voicemail
      ? 'voicemail'
      : null;
  const hasRecording =
    !!call.id && !!(call.recording_url || call.external_conversation_id);
  return (
    <TimelineRow
      actions={
        call.external_conversation_id ? (
          <Button
            href={`https://elevenlabs.io/app/agents/history/${call.external_conversation_id}`}
            icon={<ExportOutlined />}
            rel="noopener noreferrer"
            size="small"
            target="_blank"
          >
            ElevenLabs
          </Button>
        ) : undefined
      }
      muted={incoming}
      source="call"
      timestamp={call.created_at}
      timezone={timezone}
      title={
        <span style={{ textTransform: 'capitalize' }}>
          {incoming ? 'Inbound call' : 'System call'}
          {outcome ? ` · ${outcome}` : ''}
        </span>
      }
    >
      <Space direction="vertical" size={8} style={{ width: '100%' }}>
        <div
          style={{
            alignItems: 'center',
            color: '#595959',
            display: 'flex',
            flexWrap: 'wrap',
            gap: '6px 16px',
          }}
        >
          <Space size={6}>
            <PhoneOutlined />
            <Text type="secondary">{getCallDirectionLabel(incoming)}</Text>
          </Space>
          {call.call_type ? (
            <Text style={{ textTransform: 'capitalize' }} type="secondary">
              {humanize(call.call_type)}
            </Text>
          ) : null}
          {call.duration != null ? (
            <Space size={6}>
              <ClockCircleOutlined />
              <Text type="secondary">{formatDuration(call.duration)}</Text>
            </Space>
          ) : null}
          {hasRecording && !audio?.url ? (
            <AudioControl
              audio={audio}
              label="Listen to call"
              onLoad={onLoadAudio}
            />
          ) : null}
        </div>
        {hasRecording && audio?.url ? (
          <AudioControl
            audio={audio}
            label="Listen to call"
            onLoad={onLoadAudio}
          />
        ) : null}
        {call.transcript ? (
          <PreviewCollapse
            label="Transcript"
            preview={compactPreview(call.transcript)}
          >
            <Paragraph style={{ margin: 0, whiteSpace: 'pre-wrap' }}>
              {call.transcript}
            </Paragraph>
          </PreviewCollapse>
        ) : null}
      </Space>
    </TimelineRow>
  );
};

const RoutingAttemptRow = ({
  attempt,
  timezone,
  audio,
  onLoadAudio,
}: {
  attempt: RoutingAttempt;
  timezone: string;
  audio: AudioState | undefined;
  onLoadAudio: () => void;
}) => {
  const connected = !!attempt.was_routed;
  const answered = !!attempt.answered_at;
  const status = connected ? 'connected' : attempt.call_status || 'pending';
  return (
    <List.Item>
      <Space direction="vertical" size={4} style={{ width: '100%' }}>
        <div
          style={{
            display: 'flex',
            justifyContent: 'space-between',
            flexWrap: 'wrap',
            gap: 8,
          }}
        >
          <Space size="small" wrap>
            <Text strong>{attempt.agent_name || 'Unknown agent'}</Text>
            {attempt.agent_id ? (
              <Text copyable={{ text: attempt.agent_id }} type="secondary">
                {attempt.agent_id}
              </Text>
            ) : null}
            <Tag color={statusColor(status)}>{humanize(status)}</Tag>
          </Space>
          {attempt.created_at ? (
            <Tooltip title={timezone}>
              <Text style={{ fontSize: 12 }} type="secondary">
                {formatDateWithZone(attempt.created_at, timezone)}
              </Text>
            </Tooltip>
          ) : null}
        </div>
        {attempt.id && (answered || connected) ? (
          <AudioControl
            audio={audio}
            label="Listen to agent call"
            onLoad={onLoadAudio}
          />
        ) : null}
      </Space>
    </List.Item>
  );
};

const RoutingEvent = ({
  call,
  timezone,
  audioByKey,
  onLoadAudio,
}: {
  call: RoutingCall;
  timezone: string;
  audioByKey: Record<string, AudioState>;
  onLoadAudio: (key: string, path: string) => void;
}) => {
  const attempts = [...(call.attempts || [])].sort(compareRoutingAttempts);
  const routingDuration =
    call.routing_started_at && call.routing_completed_at
      ? Math.round(
          (parseTimelineTimestamp(call.routing_completed_at) -
            parseTimelineTimestamp(call.routing_started_at)) /
            1000,
        )
      : null;
  const routedAgent = call.routed_agent
    ? [call.routed_agent.first_name, call.routed_agent.last_name]
        .filter(Boolean)
        .join(' ')
    : '';
  const callAudioKey = `call:${call.call_id || ''}`;
  const hasCallRecording =
    !!call.call_id && !!(call.recording_url || call.external_conversation_id);
  return (
    <TimelineRow
      source="routing"
      timestamp={call.created_at}
      timezone={timezone}
      title={call.call_source ? humanize(call.call_source) : 'Call routing'}
    >
      <Space direction="vertical" size="small" style={{ width: '100%' }}>
        <Space size="small" wrap>
          {call.call_outcome ? (
            <Tag color={statusColor(call.call_outcome)}>
              {humanize(call.call_outcome)}
            </Tag>
          ) : null}
          {routingDuration != null ? (
            <Tag color="blue">Routed in {routingDuration}s</Tag>
          ) : null}
          {routedAgent ? (
            <Text>
              <CheckCircleOutlined style={{ color: '#52c41a' }} /> Connected to{' '}
              <strong>{routedAgent}</strong>
            </Text>
          ) : null}
          {call.external_conversation_id ? (
            <Button
              href={`https://elevenlabs.io/app/agents/history/${call.external_conversation_id}`}
              icon={<ExportOutlined />}
              rel="noopener noreferrer"
              size="small"
              target="_blank"
              type="link"
            >
              ElevenLabs
            </Button>
          ) : null}
        </Space>
        {hasCallRecording ? (
          <AudioControl
            audio={audioByKey[callAudioKey]}
            label="Listen to routed call"
            onLoad={() =>
              onLoadAudio(
                callAudioKey,
                `/api/lead-engagement/call/${call.call_id}/audio`,
              )
            }
          />
        ) : null}
        {attempts.length ? (
          <PreviewCollapse
            label={`${attempts.length} agent attempts`}
            preview={routingAttemptsPreview(attempts)}
          >
            <List
              dataSource={attempts}
              renderItem={attempt => {
                const attemptAudioKey = `attempt:${attempt.id || ''}`;
                return (
                  <RoutingAttemptRow
                    attempt={attempt}
                    audio={audioByKey[attemptAudioKey]}
                    key={getRoutingAttemptKey(attempt)}
                    onLoadAudio={() =>
                      onLoadAudio(
                        attemptAudioKey,
                        `/api/lead-engagement/agent-attempt/${attempt.id}/audio`,
                      )
                    }
                    timezone={timezone}
                  />
                );
              }}
              size="small"
            />
          </PreviewCollapse>
        ) : (
          <Text type="secondary">No agent attempts recorded</Text>
        )}
      </Space>
    </TimelineRow>
  );
};

const displayAttribute = (value: unknown): string => {
  if (Array.isArray(value)) {
    return value.map(displayAttribute).join(', ');
  }
  if (value && typeof value === 'object') {
    return JSON.stringify(value);
  }
  return String(value);
};

const ledgerPreview = (fact: LedgerFact): string => {
  const attributes = fact.attributes ?? {};
  const reason = fact.reason ? sentenceCase(fact.reason) : null;
  const outcome =
    attributes.outcome != null
      ? sentenceCase(displayAttribute(attributes.outcome))
      : null;
  const transferType =
    fact.event === 'transferred' && 'isa_workflow_id' in attributes
      ? '3-way introduction'
      : fact.event === 'transferred' && 'lead_call_id' in attributes
        ? 'Warm transfer'
        : null;
  const details = [
    transferType,
    reason,
    outcome && outcome !== reason ? `Outcome: ${outcome}` : null,
    attributes.trigger != null
      ? `Triggered by ${sentenceCase(displayAttribute(attributes.trigger))}`
      : null,
    attributes.previous_stage != null
      ? `Previously ${sentenceCase(
          displayAttribute(attributes.previous_stage),
        )}`
      : null,
    attributes.categories != null
      ? `Categories: ${sentenceCase(displayAttribute(attributes.categories))}`
      : null,
    fact.actor && fact.actor !== 'lead_engagement'
      ? actorLabel(fact.actor)
      : null,
    fact.source !== 'system' ? `Source: ${sentenceCase(fact.source)}` : null,
  ].filter((value): value is string => !!value);

  return Array.from(new Set(details)).slice(0, 2).join(' · ');
};

const LedgerEvent = ({
  fact,
  timezone,
}: {
  fact: LedgerFact;
  timezone: string;
}) => {
  const preview = ledgerPreview(fact);
  return (
    <TimelineRow
      source="ledger"
      timestamp={fact.created_at}
      timezone={timezone}
      title={sentenceCase(eventLabel(fact.event))}
    >
      <PreviewCollapse
        label={preview ? 'Context' : 'Event details'}
        preview={preview}
      >
        <Space direction="vertical" size={4} style={{ width: '100%' }}>
          <Text type="secondary">
            <strong>Recorded by:</strong> {actorLabel(fact.actor)}
            {fact.source !== 'system' ? ` · ${sentenceCase(fact.source)}` : ''}
          </Text>
          {fact.reason ? (
            <Text type="secondary">
              <strong>Reason:</strong> {sentenceCase(fact.reason)}
            </Text>
          ) : null}
          {fact.attributes
            ? Object.entries(fact.attributes).map(([key, value]) => (
                <Text key={key} style={{ fontSize: 12 }} type="secondary">
                  <strong>{humanize(key)}:</strong> {displayAttribute(value)}
                </Text>
              ))
            : null}
        </Space>
      </PreviewCollapse>
    </TimelineRow>
  );
};

const NoteEvent = ({
  note,
  timezone,
}: {
  note: EngagementNote;
  timezone: string;
}) => (
  <TimelineRow
    muted
    source="note"
    timestamp={note.created_at}
    timezone={timezone}
    title={`${note.header || 'Note added'}${
      note.created_by ? ` · ${note.created_by}` : ''
    }`}
  >
    <PreviewCollapse label="Note" preview={notePreview(note.body)}>
      <div
        dangerouslySetInnerHTML={{ __html: sanitizeHTML(note.body) }}
        style={{ color: '#595959', lineHeight: 1.6 }}
      />
    </PreviewCollapse>
  </TimelineRow>
);

const formatTimeWithZone = (dateString: string, timezone: string): string =>
  new Date(parseTimelineTimestamp(dateString)).toLocaleTimeString('en', {
    timeZone: timezone,
    hour: 'numeric',
    minute: '2-digit',
    timeZoneName: 'short',
  });

const ISA_RESULT_DOT_COLORS: Record<string, string> = {
  claim_won: '#52c41a',
  claim_lost: '#fa8c16',
  rejected: '#fa8c16',
  delivery_failed: '#ff4d4f',
};

const agentFirstName = (name: string | null): string =>
  name ? name.split(' ')[0] : 'Unknown agent';

const IsaChatBubble = ({
  firstName,
  text,
  timezone,
}: {
  firstName: string;
  text: IsaText;
  timezone: string;
}) => {
  const theirs = isAgentText(text);
  return (
    <div
      className={`isa-chat-message ${theirs ? 'isa-chat-theirs' : 'isa-chat-ours'}`}
    >
      <div className="isa-chat-bubble">{text.body}</div>
      <Text className="isa-chat-caption" type="secondary">
        {theirs ? `${firstName} → HouseWhisper` : `HouseWhisper → ${firstName}`}{' '}
        · {formatTimeWithZone(text.sent_at, timezone)}
      </Text>
    </div>
  );
};

const ThreeWayEvent = ({
  conversation,
  timezone,
}: {
  conversation: ThreeWayConversation;
  timezone: string;
}) => {
  const [open, setOpen] = useState(true);
  const labelFor = (message: EngagementMessage): string =>
    message.sender_role === 'lead'
      ? 'Lead'
      : message.sender_role === 'agent'
        ? conversation.agentName
          ? agentFirstName(conversation.agentName)
          : 'Agent'
        : 'HouseWhisper';
  const participants = Array.from(
    new Set(conversation.messages.map(labelFor)),
  ).join(', ');
  const count = conversation.messages.length;
  return (
    <TimelineRow
      source="threeWay"
      timestamp={conversation.messages[0].created_at}
      timezone={timezone}
      title={`${count} message${count === 1 ? '' : 's'}`}
    >
      <div className={`isa-agent-card${open ? ' isa-agent-card-open' : ''}`}>
        <button
          aria-expanded={open}
          className="isa-agent-card-header"
          onClick={() => setOpen(value => !value)}
          type="button"
        >
          <RightOutlined className="isa-agent-card-chevron" />
          <span className="isa-agent-card-title">
            <strong>3-way text</strong> ·{' '}
            <Text type="secondary">{participants}</Text>
          </span>
        </button>
        {open ? (
          <div className="isa-chat-thread">
            {conversation.messages.map((message, index) => {
              const ours =
                message.sender_role !== 'lead' &&
                message.sender_role !== 'agent';
              return (
                <div
                  className={`isa-chat-message ${ours ? 'isa-chat-ours' : 'isa-chat-theirs'}`}
                  key={message.id || index}
                >
                  <div
                    className="isa-chat-bubble"
                    style={
                      message.sender_role === 'agent'
                        ? { background: '#722ed1', color: '#fff' }
                        : ours
                          ? { background: '#1677ff', color: '#fff' }
                          : undefined
                    }
                  >
                    {message.message}
                  </div>
                  <Text className="isa-chat-caption" type="secondary">
                    {labelFor(message)} ·{' '}
                    {formatTimeWithZone(message.created_at, timezone)}
                  </Text>
                </div>
              );
            })}
          </div>
        ) : null}
      </div>
    </TimelineRow>
  );
};

const IsaAgentCard = ({
  footer,
  index,
  offer,
  timezone,
}: {
  footer?: ReactNode;
  index: number;
  offer: IsaOffer;
  timezone: string;
}) => {
  const winner = offer.status === 'claim_won';
  const [open, setOpen] = useState(winner);
  const firstName = agentFirstName(offer.agent_name);
  const texts = isaThreadTexts(offer);
  const last = texts[texts.length - 1];
  const replyAt = isaReplyAt(offer);
  const elapsed = replyAt ? formatElapsed(offer.offered_at, replyAt) : null;
  const summary = replyAt
    ? `replied${elapsed ? ` in ${elapsed}` : ''} · ${formatTimeWithZone(
        replyAt,
        timezone,
      )}`
    : offer.status === 'expired'
      ? null
      : 'no reply';
  return (
    <div
      className="isa-agent-slot"
      style={{ '--isa-row': index } as CSSProperties}
    >
      <div className={`isa-agent-card${open ? ' isa-agent-card-open' : ''}`}>
        <Tooltip
          title={`${offer.agent_name || 'Unknown agent'} · offered ${formatDateWithZone(
            offer.offered_at,
            timezone,
          )}`}
        >
          <button
            aria-expanded={open}
            className="isa-agent-card-header"
            onClick={() => setOpen(value => !value)}
            type="button"
          >
            <RightOutlined className="isa-agent-card-chevron" />
            <span className="isa-agent-card-title">
              {winner ? (
                <>
                  <span className="isa-winner-star">★</span>{' '}
                </>
              ) : null}
              <strong>{firstName}</strong> ·{' '}
              <span
                aria-hidden="true"
                style={{
                  color: ISA_RESULT_DOT_COLORS[offer.status] ?? '#bfbfbf',
                }}
              >
                ●
              </span>{' '}
              {isaOfferResultLabel(offer)}
              {summary ? (
                <>
                  {' · '}
                  <Text type="secondary">{summary}</Text>
                </>
              ) : null}
            </span>
          </button>
        </Tooltip>
        {open ? (
          <div className="isa-chat-thread">
            {texts.map((text, textIndex) => (
              <IsaChatBubble
                firstName={firstName}
                key={`${text.sent_at}:${textIndex}`}
                text={text}
                timezone={timezone}
              />
            ))}
          </div>
        ) : last ? (
          <Text className="isa-agent-card-preview" type="secondary">
            {isAgentText(last) ? '↙\uFE0E' : '↗\uFE0E'} “{last.body}”
          </Text>
        ) : null}
        {footer}
      </div>
    </div>
  );
};

const IsaTextsWithAgents = ({
  workflow,
  timezone,
}: {
  workflow: IsaWorkflow;
  timezone: string;
}) => {
  const waves = isaWaves(workflow);
  const count = workflow.offers.length;
  const firstOfferedAt = isaFirstOfferedAt(workflow.offers);
  const winnerLines = isaWinnerLines(workflow);
  if (!firstOfferedAt) {
    return isLiveTransferFollowUp(workflow) ? null : (
      <Text type="secondary">No agents were offered this lead</Text>
    );
  }
  return (
    <div className="isa-texts">
      <Text className="isa-texts-header" type="secondary">
        <TeamOutlined /> Texts with agents ·{' '}
        {count === 1
          ? 'offer sent to 1 agent'
          : `same offer sent separately to ${count} agents`}{' '}
        · {formatTimeWithZone(firstOfferedAt, timezone)}
      </Text>
      {waves.map(wave => (
        <div className="isa-wave" key={wave.wave}>
          {waves.length > 1 ? (
            <Text className="isa-wave-label" type="secondary">
              Wave {wave.wave} · {formatTimeWithZone(wave.offeredAt, timezone)}
            </Text>
          ) : null}
          <div className="isa-agent-cards">
            {wave.offers.map((offer, index) => (
              <IsaAgentCard
                footer={
                  offer.status === 'claim_won'
                    ? winnerLines.map(line => (
                        <Text
                          className="isa-winner-line"
                          key={line.text}
                          type="secondary"
                        >
                          {line.text} · {formatTimeWithZone(line.at, timezone)}
                        </Text>
                      ))
                    : null
                }
                index={index}
                key={offer.id}
                offer={offer}
                timezone={timezone}
              />
            ))}
          </div>
        </div>
      ))}
    </div>
  );
};

type IsaStepItem = {
  className?: string;
  title: string;
  description: ReactNode;
  status: 'finish' | 'wait' | 'error';
};

const isaStatusStep = (
  workflow: IsaWorkflow,
  time: (value: string) => string,
): IsaStepItem => ({
  className: workflow.completed_at ? undefined : 'isa-step-live',
  title: sentenceCase(workflow.status),
  description: workflow.completed_at ? time(workflow.completed_at) : '',
  status: workflow.completed_at
    ? workflow.status === 'completed'
      ? 'finish'
      : 'error'
    : 'wait',
});

const liveTransferStepItems = (
  workflow: IsaWorkflow,
  timezone: string,
): IsaStepItem[] => {
  const time = (value: string) => formatTimeWithZone(value, timezone);
  return [
    {
      title: `Transferred${workflow.claimed_by_agent_name ? ` · ${workflow.claimed_by_agent_name.split(' ')[0]}` : ''}`,
      description: workflow.claim_intro_sent_at
        ? time(workflow.claim_intro_sent_at)
        : '',
      status: 'finish',
    },
    isaStatusStep(workflow, time),
  ];
};

const isaStepItems = (
  workflow: IsaWorkflow,
  timezone: string,
  crmLink?: string,
): IsaStepItem[] => {
  const time = (value: string) => formatTimeWithZone(value, timezone);
  const firstOfferedAt = isaFirstOfferedAt(workflow.offers);
  const claimedOffer = isaClaimedOffer(workflow);
  const claimedAt =
    (claimedOffer && isaReplyAt(claimedOffer)) || workflow.winner_notified_at;
  const elapsed =
    firstOfferedAt && claimedAt
      ? formatElapsed(firstOfferedAt, claimedAt)
      : null;
  const appointment = workflow.crm_appointment_id ? (
    crmLink ? (
      <Tooltip
        title={`Open lead in FUB (appointment ${workflow.crm_appointment_id})`}
      >
        <a href={crmLink} rel="noopener noreferrer" target="_blank">
          FUB {workflow.crm_appointment_id}
        </a>
      </Tooltip>
    ) : (
      `FUB ${workflow.crm_appointment_id}`
    )
  ) : null;
  const visit = [
    workflow.appointment_start
      ? formatDateFromUTCtoAgent(workflow.appointment_start, 'UTC')
      : null,
    appointment,
  ].filter(Boolean);
  const steps: (IsaStepItem | null)[] = [
    {
      title: 'Booked',
      description: time(workflow.started_at),
      status: 'finish',
    },
    firstOfferedAt
      ? {
          title: `Offered (${workflow.offers.length})`,
          description: time(firstOfferedAt),
          status: 'finish',
        }
      : null,
    workflow.claimed_by_agent_name
      ? {
          title: `Claimed · ${workflow.claimed_by_agent_name.split(' ')[0]}`,
          description: claimedAt
            ? `${time(claimedAt)}${elapsed ? ` (+${elapsed})` : ''}`
            : '',
          status: 'finish',
        }
      : isaStatusStep(workflow, time),
    visit.length
      ? {
          title: 'Visit',
          description: visit.map((part, index) => (
            <Fragment key={index}>
              {index ? ' · ' : ''}
              {part}
            </Fragment>
          )),
          status: workflow.crm_appointment_id ? 'finish' : 'wait',
        }
      : null,
    workflow.claim_intro_sent_at
      ? {
          title: '3-way intro',
          description: time(workflow.claim_intro_sent_at),
          status: 'finish',
        }
      : null,
  ];
  return steps.filter((step): step is IsaStepItem => step !== null);
};

const animatedIsaWorkflows = new Set<string>();

const prefersReducedMotion = (): boolean =>
  typeof window.matchMedia === 'function' &&
  window.matchMedia('(prefers-reduced-motion: reduce)').matches;

const useEntranceAnimation = (id: string) => {
  const ref = useRef<HTMLDivElement>(null);
  const [animate] = useState(
    () => !animatedIsaWorkflows.has(id) && !prefersReducedMotion(),
  );
  const [inView, setInView] = useState(false);
  useEffect(() => {
    if (!animate) {
      return;
    }
    animatedIsaWorkflows.add(id);
    const element = ref.current;
    if (!element || typeof IntersectionObserver === 'undefined') {
      setInView(true);
      return;
    }
    const observer = new IntersectionObserver(entries => {
      if (entries.some(entry => entry.isIntersecting)) {
        setInView(true);
        observer.disconnect();
      }
    });
    observer.observe(element);
    return () => observer.disconnect();
  }, [animate, id]);
  const className = animate
    ? `isa-animate${inView ? ' isa-in-view' : ''}`
    : undefined;
  return { className, ref };
};

const IsaEvent = ({
  crmLink,
  workflow,
  timezone,
}: {
  crmLink?: string;
  workflow: IsaWorkflow;
  timezone: string;
}) => {
  const animation = useEntranceAnimation(workflow.id);
  return (
    <TimelineRow
      source="isa"
      timestamp={workflow.started_at}
      timezone={timezone}
      title={isaWorkflowTitle(workflow)}
    >
      <div className={animation.className} ref={animation.ref}>
        <Space direction="vertical" size={12} style={{ width: '100%' }}>
          <Steps
            items={
              isLiveTransferFollowUp(workflow)
                ? liveTransferStepItems(workflow, timezone)
                : isaStepItems(workflow, timezone, crmLink)
            }
            progressDot
            size="small"
          />
          <IsaTextsWithAgents timezone={timezone} workflow={workflow} />
        </Space>
      </div>
    </TimelineRow>
  );
};

export const EngagementTimeline = ({
  accessToken,
  apiUrl,
  campaignKey,
  clientId,
  crmLink,
  engagementId,
  leadId,
  agentId,
  sourceId,
  timezone,
}: EngagementTimelineProps) => {
  const authenticatedRequest = useAuthenticatedRequest(accessToken);
  const [events, setEvents] = useState<EngagementTimelineEvent[]>([]);
  const [loading, setLoading] = useState(true);
  const [failedSources, setFailedSources] = useState<string[]>([]);
  const [showEarlier, setShowEarlier] = useState(false);
  const [sourceFilter, setSourceFilter] = useState<SourceFilter>('all');
  const [audioByKey, setAudioByKey] = useState<Record<string, AudioState>>({});
  const objectUrls = useRef<string[]>([]);
  const audioSession = useRef(0);

  useEffect(() => {
    let cancelled = false;
    const notesRequest: Promise<{ data: EngagementNote[] }> = sourceId
      ? authenticatedRequest(headers =>
          axios.get(
            `${apiUrl}/api/agent/${agentId || 'unknown'}/lead/${sourceId}/notes`,
            { headers },
          ),
        )
      : Promise.resolve({ data: [] });

    setLoading(true);
    setEvents([]);
    setFailedSources([]);
    setShowEarlier(false);
    void Promise.allSettled([
      authenticatedRequest(headers =>
        axios.get<EngagementMessage[]>(
          `${apiUrl}/api/lead-engagement/${engagementId}/messages`,
          { headers },
        ),
      ),
      authenticatedRequest(headers =>
        axios.get<EngagementCall[]>(
          `${apiUrl}/api/lead-engagement/${engagementId}/calls`,
          { headers },
        ),
      ),
      authenticatedRequest(headers =>
        axios.get<RoutingCall[]>(
          `${apiUrl}/api/lead-engagement/${engagementId}/routing`,
          { headers },
        ),
      ),
      authenticatedRequest(headers =>
        axios.get<CadenceEvaluation[]>(
          `${apiUrl}/api/lead-engagement/${engagementId}/cadence-evaluations`,
          { headers },
        ),
      ),
      authenticatedRequest(headers =>
        axios.get<LedgerResponse>(
          `${apiUrl}/api/lead-engagement/leads/${leadId}/ledger`,
          { headers },
        ),
      ),
      notesRequest,
      authenticatedRequest(headers =>
        axios.get<IsaWorkflowsResponse>(
          `${apiUrl}/api/lead-engagements/${clientId}/leads/${leadId}/isa-workflows`,
          { headers },
        ),
      ),
    ] as const).then(results => {
      if (cancelled) {
        return;
      }
      const [messages, calls, routing, cadence, ledger, notes, isa] = results;
      const sourceNames = [
        'messages',
        'calls',
        'routing',
        'cadence evaluations',
        'ledger facts',
        'notes',
        'ISA workflows',
      ];
      setFailedSources(
        results.flatMap((result, index) =>
          result.status === 'rejected' ? [sourceNames[index]] : [],
        ),
      );
      setEvents(
        mergeTimelineEvents({
          messages: arrayResult(messages),
          calls: arrayResult(calls),
          routing: arrayResult(routing),
          cadenceEvaluations: arrayResult(cadence),
          ledgerFacts:
            ledger.status === 'fulfilled' &&
            Array.isArray(ledger.value.data.facts)
              ? ledger.value.data.facts
              : [],
          notes: arrayResult(notes),
          isaWorkflows:
            isa.status === 'fulfilled' &&
            Array.isArray(isa.value.data.workflows)
              ? isa.value.data.workflows
              : [],
        }),
      );
      setLoading(false);
    });
    return () => {
      cancelled = true;
    };
  }, [accessToken, agentId, apiUrl, clientId, engagementId, leadId, sourceId]);

  useEffect(() => {
    audioSession.current += 1;
    setAudioByKey({});
    return () => {
      audioSession.current += 1;
      objectUrls.current.forEach(url => URL.revokeObjectURL(url));
      objectUrls.current = [];
    };
  }, [engagementId]);

  const loadAudio = async (key: string, path: string) => {
    if (audioByKey[key]?.loading || audioByKey[key]?.url) {
      return;
    }
    const session = audioSession.current;
    setAudioByKey(current => ({
      ...current,
      [key]: { loading: true },
    }));
    try {
      const response = await authenticatedRequest(headers =>
        fetch(`${apiUrl}${path}`, {
          headers,
        }),
      );
      if (session !== audioSession.current) {
        return;
      }
      if (!response.ok) {
        setAudioByKey(current => ({
          ...current,
          [key]: {
            error:
              response.status === 404
                ? 'No recording available'
                : `Failed to load audio (${response.status})`,
          },
        }));
        return;
      }
      const audio = await response.blob();
      if (session !== audioSession.current) {
        return;
      }
      const url = URL.createObjectURL(audio);
      objectUrls.current.push(url);
      setAudioByKey(current => ({ ...current, [key]: { url } }));
    } catch {
      if (session !== audioSession.current) {
        return;
      }
      setAudioByKey(current => ({
        ...current,
        [key]: { error: 'Failed to load audio' },
      }));
    }
  };

  const filteredEvents = useMemo(
    () =>
      sourceFilter === 'all'
        ? events
        : events.filter(event => event.source === sourceFilter),
    [events, sourceFilter],
  );
  const { recent, earlier } = useMemo(
    () => partitionTimelineEvents(filteredEvents, timezone),
    [filteredEvents, timezone],
  );
  const outreachAssociations = useMemo(
    () => getCadenceOutreachAssociations(events),
    [events],
  );
  // Associations are computed from the full, unfiltered event list (so a Decisions-only view
  // still knows what happened), but a jump link only works if its target is actually mounted.
  const visibleKeys = useMemo(
    () => new Set(filteredEvents.map(event => event.key)),
    [filteredEvents],
  );

  const renderEvent = (event: EngagementTimelineEvent): ReactNode => {
    switch (event.source) {
      case 'message':
        return <MessageEvent message={event.payload} timezone={timezone} />;
      case 'call': {
        const audioKey = `call:${event.payload.id || ''}`;
        return (
          <CallEvent
            audio={audioByKey[audioKey]}
            call={event.payload}
            onLoadAudio={() =>
              void loadAudio(
                audioKey,
                `/api/lead-engagement/call/${event.payload.id}/audio`,
              )
            }
            timezone={timezone}
          />
        );
      }
      case 'routing':
        return (
          <RoutingEvent
            audioByKey={audioByKey}
            call={event.payload}
            onLoadAudio={(key, path) => void loadAudio(key, path)}
            timezone={timezone}
          />
        );
      case 'cadence': {
        const association = outreachAssociations[event.key];
        const traceUrl = event.payload.phoenix_trace_id
          ? cadenceTraceUrl(event.payload.phoenix_trace_id)
          : null;
        return (
          <EventSurface flush source="cadence">
            <div
              className="engagement-timeline-cadence-header"
              style={{
                alignItems: 'flex-start',
                display: 'flex',
                flexWrap: 'wrap',
                gap: 8,
                justifyContent: 'space-between',
                marginBottom: 6,
              }}
            >
              <SourceIdentity source="cadence" />
              <Space
                className="engagement-timeline-event-meta"
                size="small"
                wrap
              >
                {traceUrl ? (
                  <Button
                    href={traceUrl}
                    icon={<NodeIndexOutlined aria-hidden="true" />}
                    rel="noopener noreferrer"
                    size="small"
                    target="_blank"
                  >
                    View in Phoenix
                  </Button>
                ) : null}
                <Tooltip title={timezone}>
                  <Text
                    style={{ fontSize: 12, whiteSpace: 'nowrap' }}
                    type="secondary"
                  >
                    {formatDateWithZone(event.timestamp, timezone)}
                  </Text>
                </Tooltip>
              </Space>
            </div>
            <CadenceEvaluationRow
              actionOutcomes={
                association
                  ? {
                      sms: association.expectsMessage ? (
                        <CadenceActionOutcome
                          decisionTimestamp={event.timestamp}
                          event={association.message}
                          linked={
                            !association.message ||
                            visibleKeys.has(association.message.key)
                          }
                          observedLabel="Sent"
                          sourceAvailable={!failedSources.includes('messages')}
                          timezone={timezone}
                        />
                      ) : undefined,
                      call: association.expectsCall ? (
                        <CadenceActionOutcome
                          decisionTimestamp={event.timestamp}
                          event={association.call}
                          linked={
                            !association.call ||
                            visibleKeys.has(association.call.key)
                          }
                          observedLabel="Called"
                          sourceAvailable={!failedSources.includes('calls')}
                          timezone={timezone}
                        />
                      ) : undefined,
                    }
                  : undefined
              }
              embedded
              evaluation={event.payload}
              isLegacyEnrollment={!campaignKey}
              timezone={timezone}
            />
          </EventSurface>
        );
      }
      case 'ledger':
        return <LedgerEvent fact={event.payload} timezone={timezone} />;
      case 'note':
        return <NoteEvent note={event.payload} timezone={timezone} />;
      case 'threeWay':
        return (
          <ThreeWayEvent conversation={event.payload} timezone={timezone} />
        );
      case 'isa':
        return (
          <IsaEvent
            crmLink={crmLink}
            timezone={timezone}
            workflow={event.payload}
          />
        );
    }
  };

  const timeline = (timelineEvents: EngagementTimelineEvent[]) => (
    <Timeline
      items={timelineEvents.map(event => ({
        key: event.key,
        color: timelineDotColor(event),
        children: (
          <div id={timelineEventDomId(event.key)}>{renderEvent(event)}</div>
        ),
      }))}
    />
  );

  return (
    <Card
      className="engagement-timeline-card"
      size="small"
      title={`Activity${events.length ? ` (${events.length})` : ''}`}
    >
      {failedSources.length ? (
        <Alert
          message={`Some events could not be loaded: ${failedSources.join(', ')}`}
          showIcon
          style={{ marginBottom: 16 }}
          type="warning"
        />
      ) : null}
      {events.length > 0 && (
        <Segmented
          onChange={value => setSourceFilter(value as SourceFilter)}
          options={SOURCE_FILTERS}
          size="small"
          style={{ marginBottom: 16 }}
          value={sourceFilter}
        />
      )}
      {loading ? (
        <div style={{ padding: 60, textAlign: 'center' }}>
          <Spin size="large" />
        </div>
      ) : events.length === 0 ? (
        <Empty
          description="No engagement events found"
          image={Empty.PRESENTED_IMAGE_SIMPLE}
        />
      ) : filteredEvents.length === 0 ? (
        <Empty
          description="No events match the selected filters"
          image={Empty.PRESENTED_IMAGE_SIMPLE}
        />
      ) : (
        <>
          {recent.length ? timeline(recent) : null}
          {earlier.length ? (
            <Divider>
              {showEarlier ? (
                <Text type="secondary">Earlier events</Text>
              ) : (
                <Button onClick={() => setShowEarlier(true)} type="link">
                  Show {earlier.length} earlier events
                </Button>
              )}
            </Divider>
          ) : null}
          {showEarlier ? timeline(earlier) : null}
        </>
      )}
    </Card>
  );
};
